import { ConfigStateService, LocalizationService } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ConfigurationSettingsDto } from 'src/core/models/configuration-setting/configuration-settings.dto';
import { ConfigurationSettingsService } from 'src/core/services/configuration-settings/configuration-settings.service';
import { StringValidator } from 'src/core/validators/shared/string.validator';

@Component({
    selector: 'ca-genesys-integration-settings',
    templateUrl: './genesys-integration-settings.component.html',
    styleUrls: ['./genesys-integration-settings.component.scss'],
    standalone: false
})
export class GenesysIntegrationSettingsComponent implements OnInit {
  form: FormGroup;

  private readonly prefix = 'GenesysIntegration';
  private readonly clientId = `${this.prefix}.ClientId`;
  private readonly clientSecret = `${this.prefix}.ClientSecret`;
  private readonly region = `${this.prefix}.Region`;
  private readonly sourceName = `${this.prefix}.SourceName`;
  private readonly organizationId = `${this.prefix}.OrganizationId`;
  private readonly organizationName = `${this.prefix}.OrganizationName`;
  private readonly groupId = `${this.prefix}.GroupId`;
  private readonly groupName = `${this.prefix}.GroupName`;
  private readonly useQueueFilter = `${this.prefix}.UseQueueFilter`;
  private readonly queueFilterDictionary = `${this.prefix}.QueueFilterDictionary`;
  private readonly useLanguageDictionary = `${this.prefix}.UseLanguageDictionary`;
  private readonly useCustomRetentionPeriod = `${this.prefix}.UseCustomRetentionPeriod`;
  private readonly customRetentionPeriodDictionary = `${this.prefix}.CustomRetentionPeriodDictionary`;
  private readonly languageSupportType = `${this.prefix}.LanguageSupportType`;
  private readonly languageDictionary = `${this.prefix}.LanguageDictionary`;
  private readonly chatIntegrationEnabled = `${this.prefix}.ChatIntegrationEnabled`;
  private readonly ivrRecordEnabled = `${this.prefix}.IvrRecordEnabled`;
  private readonly consultRecordEnabled = `${this.prefix}.ConsultRecordEnabled`;
  private readonly holdRecordEnabled = `${this.prefix}.HoldRecordEnabled`;
  private readonly supportTypes = [
      {
        id: 1,
        value: 'Agent Skill Language',
      },
      {
        id: 2,
        value: 'Queue Language',
      },
  ];
  
  useQueueFilterCb = false;
  useLanguageDictionaryCb = false;
  chatIntegrationEnabledCb = false;
  useCustomRetentionPeriodCb = false;
  ivrRecordEnabledCb = false;
  consultRecordEnabledCb = false;
  holdRecordEnabledCb = false;
  placeholder = "";

  constructor(
    private fb: FormBuilder,
    private config: ConfigStateService,
    private settingService: ConfigurationSettingsService,
    private toastr: ToasterService,
    private localizationService: LocalizationService
  ) {
    this.chatIntegrationEnabledCb = config.getSetting(this.chatIntegrationEnabled) == 'true';
    this.useQueueFilterCb = config.getSetting(this.useQueueFilter) == 'true';
    this.useLanguageDictionaryCb = config.getSetting(this.useLanguageDictionary) == 'true';
    this.useCustomRetentionPeriodCb = config.getSetting(this.useCustomRetentionPeriod) == 'true';
    this.ivrRecordEnabledCb = config.getSetting(this.ivrRecordEnabled) == 'true';
    this.consultRecordEnabledCb = config.getSetting(this.consultRecordEnabled) == 'true';
    this.holdRecordEnabledCb = config.getSetting(this.holdRecordEnabled) == 'true';

    this.form = this.fb.group({
      clientId: this.fb.group({
        name: this.clientId,
        value: [
          this.config.getSetting(this.clientId),
          {
            validators: [Validators.required, StringValidator.whitespace],
          },
        ],
      }),
      clientSecret: this.fb.group({
        name: this.clientSecret,
        value: [
          this.config.getSetting(this.clientSecret),
          {
            validators: [StringValidator.whitespace],
          },
        ],
      }),
      region: this.fb.group({
        name: this.region,
        value: [
          this.config.getSetting(this.region),
          {
            validators: [Validators.required, StringValidator.whitespace],
          },
        ],
      }),
      sourceName: this.fb.group({
        name: this.sourceName,
        value: [
          this.config.getSetting(this.sourceName),
          {
            validators: [Validators.required, StringValidator.whitespace],
          },
        ],
      }),
      organizationId: this.fb.group({
        name: this.organizationId,
        value: [
          this.config.getSetting(this.organizationId),
          {
            validators: [Validators.required, StringValidator.whitespace],
          },
        ],
      }),
      organizationName: this.fb.group({
        name: this.organizationName,
        value: [
          this.config.getSetting(this.organizationName),
          {
            validators: [Validators.required, StringValidator.whitespace],
          },
        ],
      }),
      groupId: this.fb.group({
        name: this.groupId,
        value: [
          this.config.getSetting(this.groupId),
          {
            validators: [Validators.required, StringValidator.whitespace],
          },
        ],
      }),
      useCustomRetentionPeriod: this.fb.group({
        name: this.useCustomRetentionPeriod,
        value: [this.config.getSetting(this.useCustomRetentionPeriod)],
      }),
      customRetentionPeriodDictionary: this.fb.group({
        name: this.customRetentionPeriodDictionary,
        value: [this.config.getSetting(this.customRetentionPeriodDictionary)],
      }),
      useQueueFilter: this.fb.group({
        name: this.useQueueFilter,
        value: [this.config.getSetting(this.useQueueFilter)],
      }),
      queueFilterDictionary: this.fb.group({
        name: this.queueFilterDictionary,
        value: [this.config.getSetting(this.queueFilterDictionary)],
      }),
      useLanguageDictionary: this.fb.group({
        name: this.useLanguageDictionary,
        value: [this.config.getSetting(this.useLanguageDictionary)],
      }),
      languageSupportType: this.fb.group({
        name: this.languageSupportType,
        value: [parseInt(this.config.getSetting(this.languageSupportType))],
      }),
      languageDictionary: this.fb.group({
        name: this.languageDictionary,
        value: [this.config.getSetting(this.languageDictionary)],
      }),
      chatIntegrationEnabled: this.fb.group({
        name: this.chatIntegrationEnabled,
        value: [this.config.getSetting(this.chatIntegrationEnabled)],
      }),
      ivrRecordEnabled: this.fb.group({
        name: this.ivrRecordEnabled,
        value: [this.config.getSetting(this.ivrRecordEnabled)],
      }),
      consultRecordEnabled: this.fb.group({
        name: this.consultRecordEnabled,
        value: [this.config.getSetting(this.consultRecordEnabled)],
      }),
      holdRecordEnabled: this.fb.group({
        name: this.holdRecordEnabled,
        value: [this.config.getSetting(this.holdRecordEnabled)],
      }),
      groupName: this.fb.group({
        name: this.groupName,
        value: [
          this.config.getSetting(this.groupName),
          {
            validators: [Validators.required, StringValidator.whitespace],
          },
        ],
      }),
    });
  }

  ngOnInit(): void {}

  onUseQueueFilterChange(value: boolean) {
    if (!value) {
      this.form.controls.queueFilterDictionary.clearValidators();
    }
    this.form.controls.queueFilterDictionary.updateValueAndValidity();
  }

  onUseCustomRetentionPeriodChange(value: boolean) {
    if (!value) {
      this.form.controls.customRetentionPeriodDictionary.clearValidators();
    }
    this.form.controls.customRetentionPeriodDictionary.updateValueAndValidity();
  }

  onUseLanguageDictionaryChange(value: boolean) {
    if (!value) {
      this.form.controls.languageDictionary.clearValidators();
    }
    this.form.controls.languageDictionary.updateValueAndValidity();
  }

  onLanguageSupportTypeChanged($event: any){
    if($event == 1){
      this.placeholder = "{\"genesysLanguageId1\": \"genesysEnglish\", \"genesysLanguageId2\": \"genesysTurkish\"}";
    } else if ($event == 2){
      this.placeholder = "{\"en-us\":\"\",\"es-es\":\"\"}";
    }
  }

  onSubmitForm(eventArgs): void {
    if (!this.form.valid) {
      return;
    }

    const settings: ConfigurationSettingsDto[] = [];

    settings.push({
      settingName: this.form.get('clientId').get('name').value,
      settingValue: this.form.get('clientId').get('value').value,
    });

    if (this.form.get('clientSecret').get('value').value) {
      settings.push({
        settingName: this.form.get('clientSecret').get('name').value,
        settingValue: this.form.get('clientSecret').get('value').value,
      });
    }

    settings.push({
      settingName: this.form.get('region').get('name').value,
      settingValue: this.form.get('region').get('value').value,
    });

    settings.push({
      settingName: this.form.get('sourceName').get('name').value,
      settingValue: this.form.get('sourceName').get('value').value,
    });

    settings.push({
      settingName: this.form.get('organizationId').get('name').value,
      settingValue: this.form.get('organizationId').get('value').value,
    });

    settings.push({
      settingName: this.form.get('organizationName').get('name').value,
      settingValue: this.form.get('organizationName').get('value').value,
    });

    settings.push({
      settingName: this.form.get('groupId').get('name').value,
      settingValue: this.form.get('groupId').get('value').value,
    });

    settings.push({
      settingName: this.form.get('groupName').get('name').value,
      settingValue: this.form.get('groupName').get('value').value,
    });

    settings.push({
      settingName: this.form.get('useCustomRetentionPeriod').get('name').value,
      settingValue: this.useCustomRetentionPeriodCb.toString(),
    });

    settings.push({
      settingName: this.form.get('customRetentionPeriodDictionary').get('name').value,
      settingValue: this.form.get('customRetentionPeriodDictionary').get('value').value,
    });

    settings.push({
      settingName: this.form.get('useQueueFilter').get('name').value,
      settingValue: this.useQueueFilterCb.toString(),
    });

    settings.push({
      settingName: this.form.get('queueFilterDictionary').get('name').value,
      settingValue: this.form.get('queueFilterDictionary').get('value').value,
    });

    settings.push({
      settingName: this.form.get('useLanguageDictionary').get('name').value,
      settingValue: this.useLanguageDictionaryCb.toString(),
    });

    settings.push({
      settingName: this.form.get('languageDictionary').get('name').value,
      settingValue: this.form.get('languageDictionary').get('value').value,
    });

    settings.push({
      settingName: this.form.get('languageSupportType').get('name').value,
      settingValue: this.form.get('languageSupportType').get('value').value,
    });

    settings.push({
      settingName: this.form.get('chatIntegrationEnabled').get('name').value,
      settingValue: this.chatIntegrationEnabledCb.toString(),
    });

    settings.push({
      settingName: this.form.get('ivrRecordEnabled').get('name').value,
      settingValue: this.ivrRecordEnabledCb.toString(),
    });

    settings.push({
      settingName: this.form.get('consultRecordEnabled').get('name').value,
      settingValue: this.consultRecordEnabledCb.toString(),
    });

    settings.push({
      settingName: this.form.get('holdRecordEnabled').get('name').value,
      settingValue: this.holdRecordEnabledCb.toString(),
    });

    this.settingService.saveSetting(settings).subscribe(res => {
      this.toastr.success(
        this.localizationService.instant('AbpSettingManagement::SavedSuccessfully')
      );
    });
  }
}
